<template>
  <div class="pin-code">
    <div class="pin-code__top" :class="{ 'pin-code__top--set':  type === 'set' && filled}">
      <div class="pin-code__logo">
        <svg class="pin-code__logo-icon">
          <use xlink:href="#icon-logo-planet"></use>
        </svg>
      </div>
      <div class="pin-code__merchant">{{ type === 'set' ? $t('components.PinCode.Title') : merchantSlug }}</div>
      <div class="pin-code__counter" :class="{ 'shake': pinCodeInputError }">
        <div class="pin-code__counter-item" v-for="(item, index) in 4" :key="item" :class="{
          'pin-code__counter-item--active': counterActive[index],
          'pin-code__counter-item--error': pinCodeInputError,
          'pulse': loading
        }"></div>
      </div>
    </div>
    <div class="pin-code__bottom" :class="{ 'pin-code__bottom--set':  type === 'set' && filled }">
      <a class="pin-code__number" @click="concatCode('1')">1</a>
      <a class="pin-code__number" @click="concatCode('2')">2</a>
      <a class="pin-code__number" @click="concatCode('3')">3</a>
      <a class="pin-code__number" @click="concatCode('4')">4</a>
      <a class="pin-code__number" @click="concatCode('5')">5</a>
      <a class="pin-code__number" @click="concatCode('6')">6</a>
      <a class="pin-code__number" @click="concatCode('7')">7</a>
      <a class="pin-code__number" @click="concatCode('8')">8</a>
      <a class="pin-code__number" @click="concatCode('9')">9</a>
      <a class="pin-code__number pin-code__number--exit" @click="reset()">{{ $t('widgets.ConfirmModal.cancel') }}</a>
      <a class="pin-code__number" @click="concatCode('0')">0</a>
      <a class="pin-code__number" @click="erase()"><span class="icon-erase"></span></a>
    </div>

    <transition name="slide-bottom">
      <div class="pin-code__save-area pb-2" v-if="type === 'set' && filled" @click="savePin()">
        <v-button :loading="loading" class="pin-code__save-btn" color="success" width="full" size="large" :disabled="!filled">{{ $t('widgets.Dashboard.EditWidget.save') }}</v-button>
      </div>
    </transition>

    <vue-recaptcha
      ref="recaptcha"
      size="invisible"
      sitekey="6Lcll9oZAAAAAHnAmSi3s99CN6dvyITQODxou4X5"
      loadRecaptchaScript
      @verify="checkVerify"
    ></vue-recaptcha>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'

import { setPinToken, merchantSlug, loginByPinCode, pinTokenAttempts, removePinToken } from '@/mixins/pinToken'
import { notifyGraphqlError } from '@/mixins/handleGraphqlError'

import vButton from '@/ui/controls/vButton'
import { onLogin } from '@/vue-apollo'

export default {
  components: {
    VueRecaptcha,

    vButton
  },

  props: {
    /**
     * @values input, set
     */
    type: {
      type: String,
      default: 'input'
    },

    credentials: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      code: '',
      loading: false,
      reCaptcha: null,
      pinCodeInputError: false
    }
  },

  mixins: [setPinToken, notifyGraphqlError, merchantSlug, loginByPinCode, pinTokenAttempts, removePinToken],

  methods: {
    checkVerify (e) {
      this.reCaptcha = e
      this.login()
    },

    async login () {
      try {
        this.loading = true
        const credentials = await this.loginByPinCode(this.code, this.reCaptcha)
        if (!credentials.accessToken) {
          this.code = ''
          this.loading = false
          return
        }
        await onLogin(this.$apollo.getClient(), credentials.accessToken, credentials.refreshToken)
        this.recaptchaReset()
        this.loading = false
        await this.$router.push({
          name: 'dashboard'
        })
        this.recaptchaReset()
      } catch (e) {
        setTimeout(() => {
          this.pinCodeInputError = true
        }, 50)
        setTimeout(() => {
          this.pinCodeInputError = false
        }, 1000)
        this.code = ''
        this.loading = false
        this.recaptchaReset()
      }
    },

    recaptchaReset () {
      this.reCaptcha = null
      if (this.$refs.recaptcha) {
        this.$refs.recaptcha.reset()
      }
    },

    concatCode (number) {
      if (this.code.length < 4 && !this.loading) {
        this.code = this.code + number
      }
    },

    erase () {
      if (this.code.length > 0) {
        this.code = this.code.substr(0, this.code.length - 1)
      }
    },

    async reset () {
      if (this.type === 'input') {
        await this.removePinToken()
        this.$emit('reset')
        return
      }
      await this.$router.push({
        name: 'dashboard'
      })
    },

    async savePin () {
      try {
        await this.setPinToken({
          ...this.credentials,
          pin: this.code
        })

        await this.$router.push({
          name: 'dashboard'
        })
      } catch (e) {
        this.notifyGraphqlError(e)
      }
    }
  },

  computed: {
    counterActive () {
      return [this.code.length > 0, this.code.length > 1, this.code.length > 2, this.code.length > 3]
    },

    filled () {
      return this.code.length === 4
    }
  },

  watch: {
    async code (val) {
      if (val.length === 4 && this.type === 'input') {
        if (this.pinTokenAttempts < 5) {
          this.$refs.recaptcha.execute()
          return
        }

        await this.removePinToken()
        this.$emit('reset')
      }
    },

    async pinTokenAttempts () {
      if (this.pinTokenAttempts >= 5) {
        setTimeout(async () => {
          await this.removePinToken()
          this.$emit('reset')
        }, 500)
      }
    }
  }
}
</script>

<style lang="scss">
.pin-code {
  @include computedVh(100);

  &__top {
    height: 50%;
    padding: $default-spacing-mobile $default-spacing-mobile 3rem;
    transition: all .2s;

    display: flex;
    flex-direction: column;
    justify-content: center;

    &--set {
      height: calc(50% - 6.2rem);
    }
  }

  &__merchant {
    width: 100%;
    text-align: center;
    font-weight: 500;
    font-size: 1.6rem;
  }

  &__logo {
    display: none;
    justify-content: center;
    margin-bottom: auto;
    margin-top: 1rem;

    &-icon{
      width: 17.5rem;
      height: 4rem;
    }
  }

  &__counter {
    padding-top: 2rem;
    padding-bottom: 1rem;

    display: flex;
    justify-content: center;

    &-item {
      width: 1.2rem;
      height: 1.2rem;
      margin: .5rem;
      border-radius: 50%;
      background-color: $color-secondary;
      opacity: 0.4;
      transition: all .2s;

      &--active {
        opacity: 1;
      }

      &--error {
        background-color: $color-error;
        transform: scale(1.2);
        opacity: .8;
      }
    }
  }

  &__bottom {
    height: 50%;
    background-color: $color-azure-light;
    border-top-left-radius: 2rem;
    border-top-right-radius: 2rem;
    overflow: hidden;

    transition: all .2s;

    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;

    //padding: 1rem;

    @include iosPaddingBottom(1rem);

    & > * {
      width: calc((100vw)/3);
      height: calc((50vh)/4);

      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  &__save-area {
    height: 6.2rem;
  }

  &__save-btn {
    border-radius: 0;
    height: 6.2rem;
  }

  &__number, &__number:link {
    font-size: 3.2rem;
    font-weight: 500;
    cursor: pointer;
    //border-radius: 50%;
    transition: all .1s;

    &:active {
      background-color: darken($color-azure-light, 5%);
    }
  }

  &__number--exit {
    font-size: 2rem;
  }

  &__merhant {

  }
}
</style>
